import React, { useState, useEffect } from "react";
import clsx from "clsx";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(8, 8, 3),
	},

	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,

	},

	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},

	businessHoursPaper: {
		flexDirection: "column",
		alignItems: "stretch",
	},

	businessHoursRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
	},

	workingDaysGroup: {
		marginTop: 8,
		flexWrap: "wrap",
	},
}));

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const formatHour = hour => {
	const period = hour < 12 ? "a. m." : "p. m.";
	const displayHour = hour % 12 === 0 ? 12 : hour % 12;
	return `${displayHour}:00 ${period}`;
};

const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0];

const Settings = () => {
	const classes = useStyles();

	const [settings, setSettings] = useState([]);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const { value } = settings.find(s => s.key === key);
		return value;
	};

	const handleChangeWorkingDays = async (e, newDays) => {
		if (!newDays || newDays.length === 0) return;

		try {
			await api.put(`/settings/businessWorkingDays`, {
				value: [...newDays].sort().join(","),
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container} maxWidth="sm">
				<Typography variant="body2" gutterBottom>
					{i18n.t("settings.title")}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="body1">
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>

				</Paper>

				{settings && settings.length > 0 && (
					<Paper className={clsx(classes.paper, classes.businessHoursPaper)}>
						<div className={classes.businessHoursRow}>
							<Typography variant="body1">
								{i18n.t("settings.settings.postReplySnooze.name")}
							</Typography>
						</div>
						<div className={classes.businessHoursRow}>
							<Typography variant="body2">
								{i18n.t("settings.settings.postReplySnooze.secondsLabel")}
							</Typography>
							<TextField
								id="postReplySnoozeWindowSeconds-setting"
								name="postReplySnoozeWindowSeconds"
								type="number"
								margin="dense"
								variant="outlined"
								inputProps={{ min: 0, max: 300, step: 1 }}
								className={classes.settingOption}
								value={getSettingValue("postReplySnoozeWindowSeconds")}
								onChange={handleChangeSetting}
							/>
						</div>
					</Paper>
				)}

				{settings && settings.length > 0 && (
					<Paper className={clsx(classes.paper, classes.businessHoursPaper)}>
						<div className={classes.businessHoursRow}>
							<Typography variant="body1">
								{i18n.t("settings.settings.businessDayStartHour.name")}
							</Typography>
							<Select
								margin="dense"
								variant="outlined"
								native
								id="businessDayStartHour-setting"
								name="businessDayStartHour"
								value={getSettingValue("businessDayStartHour")}
								className={classes.settingOption}
								onChange={handleChangeSetting}
							>
								{HOURS.map(hour => (
									<option key={hour} value={hour}>
										{formatHour(hour)}
									</option>
								))}
							</Select>
						</div>

						<div>
							<Typography variant="body1">
								{i18n.t("settings.settings.businessWorkingDays.name")}
							</Typography>
							<ToggleButtonGroup
								className={classes.workingDaysGroup}
								value={getSettingValue("businessWorkingDays")
									.split(",")
									.map(value => Number(value.trim()))
									.filter(value => Number.isInteger(value))}
								onChange={handleChangeWorkingDays}
							>
								{WEEK_DAYS.map(day => (
									<ToggleButton key={day} value={day} size="small">
										{i18n.t(`settings.settings.businessWorkingDays.days.${day}`)}
									</ToggleButton>
								))}
							</ToggleButtonGroup>
						</div>
					</Paper>
				)}

				{settings && settings.length > 0 && (
					<Paper className={clsx(classes.paper, classes.businessHoursPaper)}>
						<div className={classes.businessHoursRow}>
							<Typography variant="body1">
								{i18n.t("settings.settings.oldTicketsReminder.name")}
							</Typography>
						</div>

						<div className={classes.businessHoursRow}>
							<Typography variant="body2">
								{i18n.t("settings.settings.oldTicketsReminder.enabledLabel")}
							</Typography>
							<Select
								margin="dense"
								variant="outlined"
								native
								id="oldTicketsReminderEnabled-setting"
								name="oldTicketsReminderEnabled"
								value={getSettingValue("oldTicketsReminderEnabled")}
								className={classes.settingOption}
								onChange={handleChangeSetting}
							>
								<option value="true">
									{i18n.t("settings.settings.oldTicketsReminder.options.enabled")}
								</option>
								<option value="false">
									{i18n.t("settings.settings.oldTicketsReminder.options.disabled")}
								</option>
							</Select>
						</div>

						<div className={classes.businessHoursRow}>
							<Typography variant="body2">
								{i18n.t("settings.settings.oldTicketsReminder.thresholdDaysLabel")}
							</Typography>
							<TextField
								id="oldTicketsReminderThresholdDays-setting"
								name="oldTicketsReminderThresholdDays"
								type="number"
								margin="dense"
								variant="outlined"
								inputProps={{ min: 1 }}
								className={classes.settingOption}
								value={getSettingValue("oldTicketsReminderThresholdDays")}
								onChange={handleChangeSetting}
							/>
						</div>

						<div className={classes.businessHoursRow}>
							<Typography variant="body2">
								{i18n.t("settings.settings.oldTicketsReminder.intervalMinutesLabel")}
							</Typography>
							<TextField
								id="oldTicketsReminderIntervalMinutes-setting"
								name="oldTicketsReminderIntervalMinutes"
								type="number"
								margin="dense"
								variant="outlined"
								inputProps={{ min: 1 }}
								className={classes.settingOption}
								value={getSettingValue("oldTicketsReminderIntervalMinutes")}
								onChange={handleChangeSetting}
							/>
						</div>
					</Paper>
				)}

				<Paper className={classes.paper}>
					<TextField
						id="api-token-setting"
						readonly
						label="Token Api"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>

			</Container>
		</div>
	);
};

export default Settings;
